<template>
    <AppHead title="Award Booking Request" />

    <div class="px-4 md:px-8 py-2 md:py-4">
        <!-- Loading -->
        <div v-if="detailLoading && !booking" class="flex justify-center items-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>

        <template v-else-if="booking">
            <!-- Header -->
            <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
                <div>
                    <Link
                        href="/award-bookings"
                        class="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 inline-flex items-center gap-1 mb-2"
                    >
                        ← All award bookings
                    </Link>
                    <div class="flex items-center gap-3">
                        <h2 class="text-2xl font-semibold text-slate-800 dark:text-slate-100">
                            {{ booking.destination }}
                        </h2>
                        <StatusBadge :status="booking.status" />
                    </div>
                    <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">
                        {{ dateLine }}
                    </p>
                </div>
            </div>

            <!-- Awaiting payment banner -->
            <div
                v-if="booking.status === 'pending_payment' && payableDeposit"
                class="mb-6 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
                <div>
                    <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
                        Your search fee payment wasn't completed
                    </p>
                    <p class="text-sm text-slate-600 dark:text-slate-300 mt-0.5">
                        Your request isn't with our booking team yet — complete the
                        ${{ Number(payableDeposit.amount).toFixed(2) }} search fee to submit it.
                    </p>
                </div>
                <ActionButton
                    label="Complete payment"
                    :loading="payingPaymentRequestId === payableDeposit.id"
                    button-id="complete-deposit"
                    classes="shrink-0 inline-flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors"
                    @clicked="pay(payableDeposit)"
                />
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Thread -->
                <div class="lg:col-span-2">
                    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col">
                        <div class="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
                            <h3 class="font-semibold text-slate-800 dark:text-slate-100">Messages</h3>
                            <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                All correspondence about this booking happens here — we'll email you when there's something new.
                            </p>
                        </div>

                        <div class="p-5 space-y-5 max-h-[32rem] overflow-y-auto" ref="threadEl">
                            <p
                                v-if="messages.length === 0"
                                class="text-sm text-slate-500 dark:text-slate-400 text-center py-8"
                            >
                                No messages yet — our booking team will follow up here soon.
                            </p>
                            <MessageItem
                                v-for="message in messages"
                                :key="message.id"
                                :message="message"
                                :payment-request="paymentRequestFor(message)"
                                :paying="payingPaymentRequestId === message.payment_request_id"
                                @pay="pay"
                            />
                        </div>

                        <!-- Composer -->
                        <div class="px-5 py-4 border-t border-slate-200 dark:border-slate-700">
                            <textarea
                                v-model="newMessage"
                                rows="3"
                                placeholder="Write a message to the booking team…"
                                class="w-full rounded-lg border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 text-sm focus:border-orange-500 focus:ring-orange-500"
                            ></textarea>
                            <div class="flex items-start justify-between gap-3 mt-2">
                                <AttachmentPicker
                                    v-model="pendingAttachments"
                                    :booking-uuid="bookingUuid"
                                    class="min-w-0 flex-1"
                                />
                                <ActionButton
                                    label="Send message"
                                    :loading="sendingMessage"
                                    :disabled="!canSend"
                                    button-id="send-message"
                                    classes="inline-flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                                    @clicked="send"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sidebar -->
                <div class="space-y-6">
                    <!-- Fee summary -->
                    <div
                        v-if="fees && fees.quoted_fee !== null"
                        class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-5"
                    >
                        <h3 class="font-semibold text-slate-800 dark:text-slate-100 mb-3">Your fee</h3>
                        <div class="space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
                            <div class="flex justify-between">
                                <span>Total booking fee</span>
                                <span class="font-semibold text-slate-900 dark:text-white">${{ formatMoney(fees.quoted_fee) }}</span>
                            </div>
                            <div v-if="fees.credit_applied > 0" class="flex justify-between">
                                <span>Your 10xT+ credit</span>
                                <span>−${{ formatMoney(fees.credit_applied) }}</span>
                            </div>
                            <div v-if="fees.legacy_discount_amount > 0" class="flex justify-between">
                                <span>Member discount ({{ Math.round(fees.legacy_discount_rate * 100) }}%)</span>
                                <span>−${{ formatMoney(fees.legacy_discount_amount) }}</span>
                            </div>
                            <div v-if="fees.promo_discount > 0" class="flex justify-between">
                                <span>Promo code</span>
                                <span>−${{ formatMoney(fees.promo_discount) }}</span>
                            </div>
                            <div
                                v-if="fees.credit_applied > 0 || fees.legacy_discount_amount > 0 || fees.promo_discount > 0"
                                class="flex justify-between border-t border-slate-100 dark:border-slate-700 pt-1.5"
                            >
                                <span>Your total</span>
                                <span class="font-medium text-slate-800 dark:text-slate-100">${{ formatMoney(fees.discounted_total) }}</span>
                            </div>
                            <div class="flex justify-between">
                                <span>Search fee (credited)</span>
                                <span>−${{ formatMoney(fees.deposit) }}</span>
                            </div>
                            <div class="flex justify-between border-t border-slate-100 dark:border-slate-700 pt-1.5">
                                <span>Balance due</span>
                                <span class="font-semibold text-slate-900 dark:text-white">${{ formatMoney(fees.balance_due) }}</span>
                            </div>
                        </div>
                        <p class="text-xs text-slate-400 dark:text-slate-500 mt-2">
                            The balance is only charged if we complete your booking —
                            we'll send a payment link here when it's time.
                        </p>
                    </div>

                    <!-- Payments -->
                    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-5">
                        <h3 class="font-semibold text-slate-800 dark:text-slate-100 mb-3">Payments</h3>
                        <div class="space-y-3">
                            <PaymentRequestCard
                                v-for="paymentRequest in paymentRequests"
                                :key="paymentRequest.id"
                                :payment-request="paymentRequest"
                                :paying="payingPaymentRequestId === paymentRequest.id"
                                @pay="pay"
                            />
                            <p
                                v-if="paymentRequests.length === 0"
                                class="text-sm text-slate-500 dark:text-slate-400"
                            >
                                No payments yet.
                            </p>
                        </div>
                    </div>

                    <!-- Request details -->
                    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-5">
                        <button
                            type="button"
                            class="w-full flex items-center justify-between"
                            @click="detailsOpen = !detailsOpen"
                        >
                            <h3 class="font-semibold text-slate-800 dark:text-slate-100">Request details</h3>
                            <Icon
                                :icon="detailsOpen ? 'chevron-up' : 'chevron-down'"
                                :width="16"
                                :height="16"
                                class="text-slate-400"
                            />
                        </button>
                        <dl v-show="detailsOpen" class="mt-3 space-y-2.5">
                            <div v-for="(value, label) in booking.summary_fields" :key="label">
                                <dt class="text-xs text-slate-500 dark:text-slate-400">{{ label }}</dt>
                                <dd class="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-line">{{ value }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Link } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import { useAwardBookingsStore } from '@/store/award-bookings';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ActionButton from '@/components/action-button.vue';
import StatusBadge from '@/components/award-bookings/status-badge.vue';
import MessageItem from '@/components/award-bookings/message-item.vue';
import AttachmentPicker from '@/components/award-bookings/attachment-picker.vue';
import PaymentRequestCard from '@/components/award-bookings/payment-request-card.vue';

const props = defineProps({
    bookingUuid: { type: String, required: true },
});

const store = useAwardBookingsStore();
const { currentBooking, detailLoading, sendingMessage, payingPaymentRequestId } = storeToRefs(store);

const newMessage = ref('');
const pendingAttachments = ref([]);
const detailsOpen = ref(false);
const threadEl = ref(null);

const booking = computed(() => currentBooking.value?.booking || null);
const fees = computed(() => booking.value?.fee_breakdown || null);
const messages = computed(() => currentBooking.value?.messages || []);
const paymentRequests = computed(() => currentBooking.value?.payment_requests || []);

const payableDeposit = computed(() =>
    paymentRequests.value.find((paymentRequest) => paymentRequest.type === 'deposit' && paymentRequest.payable)
);

const dateLine = computed(() => {
    if (!booking.value) {
        return '';
    }
    const range = booking.value.end_date
        ? `${booking.value.start_date} – ${booking.value.end_date}`
        : booking.value.start_date;
    const travelers = `${booking.value.travelers_count} traveler${booking.value.travelers_count !== 1 ? 's' : ''}`;
    return `${range} · ${travelers}`;
});

const formatMoney = (value) =>
    Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const paymentRequestFor = (message) => {
    if (!message.payment_request_id) {
        return null;
    }
    return paymentRequests.value.find((paymentRequest) => paymentRequest.id === message.payment_request_id) || null;
};

const scrollThreadToBottom = async () => {
    await nextTick();
    if (threadEl.value) {
        threadEl.value.scrollTop = threadEl.value.scrollHeight;
    }
};

watch(messages, scrollThreadToBottom);

const uploadedAttachmentIds = computed(() =>
    pendingAttachments.value.filter((item) => item.id).map((item) => item.id)
);

const uploadsInFlight = computed(() => pendingAttachments.value.some((item) => !item.id && !item.error));

// Send is blocked while a file is still uploading, so nothing the user
// picked is silently left behind.
const canSend = computed(
    () =>
        !uploadsInFlight.value &&
        (newMessage.value.trim() !== '' || uploadedAttachmentIds.value.length > 0)
);

const send = async () => {
    if (!canSend.value) {
        return;
    }
    await store.sendMessage(props.bookingUuid, newMessage.value.trim(), uploadedAttachmentIds.value);
    newMessage.value = '';
    // Failed files were never sent: keep their chips so the customer sees
    // that, and can remove or re-pick them.
    pendingAttachments.value = pendingAttachments.value.filter((item) => item.error);
};

const pay = async (paymentRequest) => {
    const checkoutUrl = await store.payPaymentRequest(props.bookingUuid, paymentRequest.id);
    window.location.assign(checkoutUrl);
};

const handlePaymentReturn = () => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('payment');
    if (!outcome) {
        return;
    }

    const toasts = {
        success: {
            type: 'success',
            title: 'Payment received',
            text: 'Thanks — your payment went through.',
        },
        pending: {
            type: 'success',
            title: 'Payment processing',
            text: "Stripe is confirming your payment — this page will reflect it shortly.",
        },
        cancelled: {
            type: 'error',
            title: 'Payment not completed',
            text: 'No charge was made. You can retry the payment whenever you are ready.',
        },
    };

    if (toasts[outcome]) {
        notify({ group: 'notifications', ...toasts[outcome] });
    }

    // Strip the query so refreshes don't re-toast.
    params.delete('payment');
    params.delete('session_id');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
};

onMounted(async () => {
    handlePaymentReturn();
    await store.fetchBooking(props.bookingUuid);
    await scrollThreadToBottom();
});
</script>
